import { useEffect } from 'react';
import { Navigate, Outlet, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { logout, markSessionAnonymous, restoreSession, selectIsAuthenticated, selectSessionChecked, useGetSessionQuery } from '@features/auth';
import { initializeProject } from '@features/projects';
import { SESSION_EXPIRED_EVENT } from '@core/api/sessionFetch';

/**
 * Protected Route Component
 *
 * Redirects to login if not authenticated.
 */
export function ProtectedRoute() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const sessionChecked = useAppSelector(selectSessionChecked);
  const { data, isLoading, isError } = useGetSessionQuery(undefined, {
    skip: isAuthenticated || sessionChecked !== false,
  });

  useEffect(() => {
    if (data?.user) {
      dispatch(restoreSession({ user: data.user, csrfToken: data.csrf_token }));
      dispatch(initializeProject(data.user));
    } else if (isError) {
      dispatch(markSessionAnonymous());
    }
  }, [data, dispatch, isError]);

  useEffect(() => {
    const handleSessionExpired = () => {
      dispatch(logout());
      navigate('/login', { replace: true, state: { sessionExpired: true } });
    };
    globalThis.addEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
    return () => globalThis.removeEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
  }, [dispatch, navigate]);

  if (!isAuthenticated && (isLoading || sessionChecked === false)) {
    return <div className="flex min-h-screen items-center justify-center text-sm text-[var(--color-text-muted)]">{t('core.restoringSession')}</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <Outlet />;
}
